// UK postcode validation (covers all valid UK postcode formats)
export const validateUKPostcode = (postcode) => {
  if (!postcode || typeof postcode !== 'string') return false;
  const cleaned = postcode.replace(/\s/g, '').toUpperCase();
  return /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i.test(cleaned);
};

// US ABA routing number: 9 digits, checksum (mod 10) per Federal Reserve algorithm
export const validateUSRoutingNumber = (routing) => {
  if (!routing || typeof routing !== 'string') return false;
  const cleaned = routing.replace(/\D/g, '');
  if (cleaned.length !== 9) return false;
  const d = cleaned.split('').map(Number);
  const checksum =
    (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])) % 10;
  return checksum === 0;
};

// US bank account number (ACH / domestic): typically 4–17 digits
export const validateUSAccountNumber = (accountNumber) => {
  if (!accountNumber || typeof accountNumber !== 'string') return false;
  const cleaned = accountNumber.replace(/\D/g, '');
  return cleaned.length >= 4 && cleaned.length <= 17;
};

// UK sort code: 6 digits, optionally with hyphens (e.g. 12-34-56 or 123456)
export const validateUKSortCode = (sortCode) => {
  if (!sortCode || typeof sortCode !== 'string') return false;
  const cleaned = sortCode.replace(/\D/g, '');
  return cleaned.length === 6 && /^\d{6}$/.test(cleaned);
};

// Format UK sort code for display (XX-XX-XX)
export const formatUKSortCode = (sortCode) => {
  if (!sortCode) return '';
  const cleaned = sortCode.replace(/\D/g, '');
  if (cleaned.length !== 6) return sortCode;
  return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 4)}-${cleaned.slice(4, 6)}`;
};

// UK account number: 8 digits (standard)
export const validateUKAccountNumber = (accountNumber) => {
  if (!accountNumber || typeof accountNumber !== 'string') return false;
  const cleaned = accountNumber.replace(/\D/g, '');
  return cleaned.length === 8 && /^\d{8}$/.test(cleaned);
};

// UK phone: optional +44 or 0 prefix, then 10 digits (excluding 0)
export const validateUKPhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('44')) return cleaned.length === 12 || cleaned.length === 11;
  return cleaned.length === 10 || cleaned.length === 11;
};

// IBAN validation helper
export const validateIBAN = (iban) => {
  // Remove spaces and convert to uppercase
  const cleanedIBAN = iban.replace(/\s/g, '').toUpperCase();
  
  // Basic format check
  if (!/^[A-Z]{2}[0-9A-Z]{13,32}$/.test(cleanedIBAN)) {
    return false;
  }

  // Move the first 4 characters to the end
  const rearrangedIBAN = cleanedIBAN.slice(4) + cleanedIBAN.slice(0, 4);
  
  // Convert letters to numbers (A=10, B=11, etc.)
  const convertedIBAN = rearrangedIBAN.split('').map(char => {
    if (/[0-9]/.test(char)) {
      return char;
    }
    return (char.charCodeAt(0) - 55).toString();
  }).join('');
  
  // Calculate mod-97
  let remainder = '';
  for (let i = 0; i < convertedIBAN.length; i++) {
    remainder = (remainder + convertedIBAN[i]) % 97;
  }
  
  // Valid IBAN should give remainder of 1
  return remainder === 1;
};

// SWIFT/BIC code validation
export const validateSWIFT = (swift) => {
  const cleanedSWIFT = swift.replace(/\s/g, '').toUpperCase();
  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(cleanedSWIFT);
};

// Currency code validation
export const validateCurrencyCode = (code) => {
  return /^[A-Z]{3}$/.test(code);
};

// Amount validation
export const validateAmount = (amount, min = 0.01) => {
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) && numAmount >= min;
}; 